import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Check, AlertCircle, AlertTriangle, FileSpreadsheet, ShieldCheck } from 'lucide-react';
import ApiService from '../services/ApiService';

const rowKey = (row) => `${row.tabName}::${row.rowNumber}`;

// True only when the raw cell had actual text that can't be parsed as a number ("bids", "2 pad", etc.)
// A blank cell or a cell containing a valid number (including "0") does NOT trigger this.
const isNonNumeric = (raw) => {
  if (!raw || raw.trim() === '') return false;
  const cleaned = raw.replace(/[$,]/g, '').trim();
  return isNaN(Number(cleaned));
};

const hasWarning = (row) =>
  isNonNumeric(row.amountPaidToUsRaw) ||
  isNonNumeric(row.amountPaidToSubRaw) ||
  // Treat a blank/zero "paid to us" as not ready — there's nothing to invoice
  (!row.amountPaidToUs && !isNonNumeric(row.amountPaidToUsRaw));

const AmountInput = ({ row, field, rawField, overrides, updateOverride }) => {
  const raw = row[rawField] || '';
  const parsed = row[field];
  const key = rowKey(row);
  const hasOverride = overrides[key]?.[field] !== undefined;
  const hasTextInSheet = isNonNumeric(raw) && !hasOverride;

  // Blank sheet cell → show empty input; numeric cell → show the number; override → show override value
  let currentVal;
  if (hasOverride) {
    currentVal = overrides[key][field];
  } else if (!raw || raw.trim() === '') {
    currentVal = '';
  } else {
    currentVal = parsed;
  }

  return (
    <div>
      <input
        type="number"
        min="0"
        step="0.01"
        className={`border rounded px-2 py-1 w-24 text-right text-sm ${
          hasTextInSheet
            ? 'border-orange-400 bg-orange-50 ring-1 ring-orange-400'
            : 'border-gray-200'
        }`}
        value={currentVal}
        onChange={(e) => updateOverride(row, field, e.target.value)}
        placeholder="$0.00"
        title={hasTextInSheet ? `Sheet had: "${raw}" — enter the dollar amount` : undefined}
      />
      {hasTextInSheet && (
        <div className="relative group inline-block max-w-28 mt-0.5">
          <div className="text-xs text-orange-600 truncate">
            ⚠ sheet: "{raw}"
          </div>
          <div className="pointer-events-none absolute left-0 top-full z-10 mt-1 hidden group-hover:block whitespace-normal break-words rounded border border-orange-300 bg-white px-2 py-1 text-xs text-orange-700 shadow-lg w-max max-w-xs">
            Sheet: "{raw}"
          </div>
        </div>
      )}
    </div>
  );
};

const SheetImport = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [overrides, setOverrides] = useState({});
  const [approving, setApproving] = useState(false);
  const [results, setResults] = useState(null);
  const [activeTab, setActiveTab] = useState(null);
  const [filterClean, setFilterClean] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [auditRows, setAuditRows] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);

  const loadPending = useCallback(async () => {
    setLoading(true);
    setError(null);
    setResults(null);
    try {
      const data = await ApiService.getSheetImportPending();
      const rows = data || [];
      setRows(rows);
      setSelectedKeys(new Set()); // start unchecked — user selects what to import
      setOverrides({});
      // Default to first tab
      const tabs = [...new Set(rows.map(r => r.tabName))];
      setActiveTab(tabs[0] || null);
    } catch (err) {
      console.error('Error loading pending sheet rows:', err);
      setError(err.message || 'Failed to load the sheet. Check GoogleSheets configuration on the server.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPending(); }, [loadPending]);

  const loadAudit = async () => {
    setAuditLoading(true);
    try {
      const data = await ApiService.getSheetAudit();
      setAuditRows(data || []);
    } catch (err) {
      console.error('Audit error:', err);
      setAuditRows([]);
    } finally {
      setAuditLoading(false);
    }
  };

  const handleAuditToggle = () => {
    setShowAudit(v => {
      if (!v && auditRows === null) loadAudit();
      return !v;
    });
  };

  const updateOverride = (row, field, value) => {
    const key = rowKey(row);
    setOverrides(prev => ({ ...prev, [key]: { ...prev[key], [field]: value } }));
  };

  const fieldValue = (row, field) => {
    const key = rowKey(row);
    return overrides[key]?.[field] !== undefined ? overrides[key][field] : row[field];
  };

  const tabs = [...new Set(rows.map(r => r.tabName))];
  const rowsForTab = (tab) => rows.filter(r => r.tabName === tab);
  const newRowsForTab = (tab) => rowsForTab(tab).filter(r => !r.alreadyExists);

  // Operates only on the rows passed in — caller is responsible for passing the visible (filtered) set.
  const toggleSelectAll = (visibleRows) => {
    const allSelected = visibleRows.every(r => selectedKeys.has(rowKey(r)));
    setSelectedKeys(prev => {
      const next = new Set(prev);
      if (allSelected) visibleRows.forEach(r => next.delete(rowKey(r)));
      else visibleRows.forEach(r => next.add(rowKey(r)));
      return next;
    });
  };

  const toggleRow = (row) => {
    const key = rowKey(row);
    setSelectedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Rows that have a non-numeric dollar cell and haven't been overridden yet
  const unresolvedWarnings = rows.filter(r => {
    const key = rowKey(r);
    const usOk = !isNonNumeric(r.amountPaidToUsRaw) || overrides[key]?.amountPaidToUs !== undefined;
    const subOk = !isNonNumeric(r.amountPaidToSubRaw) || overrides[key]?.amountPaidToSub !== undefined;
    return selectedKeys.has(key) && (!usOk || !subOk);
  });

  const handleApprove = async () => {
    const selectedRows = rows
      .filter(r => selectedKeys.has(rowKey(r)))
      .map(r => ({
        tabName: r.tabName,
        rowNumber: r.rowNumber,
        workOrderNumber: r.workOrderNumber,
        amountPaidToUs: Number(fieldValue(r, 'amountPaidToUs')) || 0,
        address: fieldValue(r, 'address'),
        amountPaidToSub: Number(fieldValue(r, 'amountPaidToSub')) || 0,
        subName: fieldValue(r, 'subName'),
        subcontractorFullName: r.subcontractorContactMatchName || null, // e.g. "Jason Bowman"
        notes: fieldValue(r, 'notes'),
        department: r.department,
        clientName: fieldValue(r, 'clientName'),
        clientContactId: fieldValue(r, 'clientContactId'),
        subcontractorContactId: fieldValue(r, 'subcontractorContactId')
      }));

    if (selectedRows.length === 0) return;
    setApproving(true);
    try {
      const result = await ApiService.approveSheetImport(selectedRows);
      setResults(result);
    } catch (err) {
      console.error('Error approving sheet import:', err);
      alert('Import failed: ' + err.message);
    } finally {
      setApproving(false);
    }
  };

  if (results) {
    return (
      <div className="space-y-6">
        <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <FileSpreadsheet className="w-6 h-6 text-blue-600" />
          Sheet Import — Results
        </h2>

        <div className={`rounded-lg p-4 ${results.errorCount > 0 ? 'bg-yellow-50' : 'bg-green-50'}`}>
          <h3 className={`text-lg font-semibold mb-3 ${results.errorCount > 0 ? 'text-yellow-800' : 'text-green-800'}`}>
            Import Complete
          </h3>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-green-600">{results.successCount}</p>
              <p className="text-sm text-gray-600">Work Orders Created</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-500">{results.alreadyExisted?.length ?? 0}</p>
              <p className="text-sm text-gray-600">Already Existed</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-red-600">{results.errorCount}</p>
              <p className="text-sm text-gray-600">Errors</p>
            </div>
          </div>
        </div>

        {results.happyWriteFailed?.length > 0 && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <h4 className="font-semibold text-orange-800 mb-2 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Imported but "happy" write failed — mark these by hand in the sheet
            </h4>
            <ul className="text-sm text-orange-700 space-y-1">
              {results.happyWriteFailed.map((wo, i) => <li key={i}>• {wo}</li>)}
            </ul>
          </div>
        )}

        {results.errorCount > 0 && (
          <div className="bg-red-50 rounded-lg p-4">
            <h4 className="font-semibold text-red-800 mb-2 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" /> Errors
            </h4>
            <ul className="text-sm text-red-700 space-y-1 max-h-40 overflow-y-auto">
              {results.errors.map((e, i) => <li key={i}>• {e}</li>)}
            </ul>
          </div>
        )}

        {results.createdWorkOrderNumbers?.length > 0 && (
          <div className="bg-green-50 rounded-lg p-4">
            <h4 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
              <Check className="w-4 h-4" /> Created — "happy" written to sheet
            </h4>
            <div className="text-sm text-green-700 columns-3 gap-4">
              {results.createdWorkOrderNumbers.map((wo, i) => <div key={i}>• {wo}</div>)}
            </div>
          </div>
        )}

        <div className="flex justify-end">
          <button
            onClick={loadPending}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Pending Rows
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <FileSpreadsheet className="w-6 h-6 text-blue-600" />
            Sheet Import
          </h2>
          <p className="text-xs text-gray-400 mt-0.5">
            Edits here are for this import only — nothing is written back to the sheet until you click Approve.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleAuditToggle}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
              showAudit
                ? 'bg-amber-100 border-amber-400 text-amber-800'
                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <ShieldCheck className="w-4 h-4" />
            Audit
          </button>
          <button
            onClick={loadPending}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Audit panel: sheet rows marked happy but missing from the app */}
      {showAudit && (
        <div className="border border-amber-300 bg-amber-50 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-amber-800 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4" />
              Audit — marked "happy" in sheet but not found in the app
            </h3>
            <button
              onClick={loadAudit}
              disabled={auditLoading}
              className="text-sm text-amber-700 hover:underline flex items-center gap-1"
            >
              <RefreshCw className={`w-3 h-3 ${auditLoading ? 'animate-spin' : ''}`} />
              Re-run
            </button>
          </div>

          {auditLoading && (
            <div className="text-sm text-amber-700 animate-pulse">Scanning sheet...</div>
          )}

          {!auditLoading && auditRows !== null && auditRows.length === 0 && (
            <div className="flex items-center gap-2 text-sm text-green-700">
              <Check className="w-4 h-4" />
              All "happy" rows in the sheet have a matching Work Order in the app.
            </div>
          )}

          {!auditLoading && auditRows && auditRows.length > 0 && (
            <>
              <p className="text-sm text-amber-700">
                <strong>{auditRows.length} row{auditRows.length > 1 ? 's are' : ' is'} marked happy in the sheet but missing from the app.</strong>{' '}
                These may have been manually marked, deleted from the app, or imported under a different tenant.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase text-amber-700 border-b border-amber-300">
                    <tr>
                      <th className="px-3 py-2 text-left">Tab</th>
                      <th className="px-3 py-2 text-left">Work Order #</th>
                      <th className="px-3 py-2 text-left">Address</th>
                      <th className="px-3 py-2 text-right">Paid to Us</th>
                      <th className="px-3 py-2 text-right">Paid to Sub</th>
                      <th className="px-3 py-2 text-left">Sub</th>
                      <th className="px-3 py-2 text-left">In Xero?</th>
                      <th className="px-3 py-2 text-left">Notes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-amber-200">
                    {auditRows.map((r, i) => (
                      <tr key={i} className="hover:bg-amber-100">
                        <td className="px-3 py-2 text-amber-700">{r.tabName}</td>
                        <td className="px-3 py-2 font-medium">{r.workOrderNumber}</td>
                        <td className="px-3 py-2">{r.address}</td>
                        <td className="px-3 py-2 text-right">{r.amountPaidToUs > 0 ? `$${r.amountPaidToUs.toFixed(2)}` : '—'}</td>
                        <td className="px-3 py-2 text-right">{r.amountPaidToSub > 0 ? `$${r.amountPaidToSub.toFixed(2)}` : '—'}</td>
                        <td className="px-3 py-2">{r.subName}</td>
                        <td className="px-3 py-2">
                          {!r.xeroChecked ? (
                            <span className="text-xs text-gray-400">Xero not connected</span>
                          ) : r.foundInXero ? (
                            <div>
                              <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${
                                r.xeroStatus === 'PAID' ? 'bg-green-100 text-green-700' :
                                r.xeroStatus === 'AUTHORISED' ? 'bg-blue-100 text-blue-700' :
                                r.xeroStatus === 'VOIDED' ? 'bg-red-100 text-red-700' :
                                'bg-gray-100 text-gray-600'
                              }`}>{r.xeroStatus}</span>
                              {r.xeroTotal != null && (
                                <div className="text-xs text-gray-500 mt-0.5">${r.xeroTotal.toFixed(2)}</div>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-red-600 flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" /> Not in Xero
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-gray-500">{r.notes}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {!loading && rows.length === 0 && !error && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center text-gray-500">
          No rows pending — every row in the configured sheet tabs is already marked "happy".
        </div>
      )}

      {/* Orange warning bar if any selected rows still have unresolved text in dollar fields */}
      {unresolvedWarnings.length > 0 && (
        <div className="bg-orange-50 border border-orange-300 rounded-lg px-4 py-3 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-orange-600 flex-shrink-0" />
          <p className="text-sm text-orange-800">
            <strong>{unresolvedWarnings.length} selected row{unresolvedWarnings.length > 1 ? 's have' : ' has'} non-numeric dollar values</strong> (highlighted in orange).
            Enter the correct dollar amounts before approving.
          </p>
        </div>
      )}

      {/* Tab buttons */}
      {tabs.length > 1 && (
        <div className="flex gap-2 border-b border-gray-200 pb-0">
          {tabs.map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === tab
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab}
              <span className="ml-1.5 text-xs text-gray-400">
                ({newRowsForTab(tab).length} pending)
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Table for active tab */}
      {activeTab && (() => {
        const tabRows = rowsForTab(activeTab);
        const allNewRows = tabRows.filter(r => !r.alreadyExists);
        const existingRows = tabRows.filter(r => r.alreadyExists);
        // Apply filter: when filterClean is on, hide rows with unresolved non-numeric dollar cells
        const newRows = filterClean ? allNewRows.filter(r => !hasWarning(r)) : allNewRows;
        const hiddenCount = allNewRows.length - newRows.length;
        const allSelected = newRows.length > 0 && newRows.every(r => selectedKeys.has(rowKey(r)));
        const someSelected = newRows.some(r => selectedKeys.has(rowKey(r)));
        const selectedInTab = newRows.filter(r => selectedKeys.has(rowKey(r))).length;

        return (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-700">{activeTab}</h3>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={filterClean}
                    onChange={() => setFilterClean(v => !v)}
                    className="rounded"
                  />
                  <span className="text-gray-600">Clean rows only</span>
                  {filterClean && hiddenCount > 0 && (
                    <span className="text-xs text-orange-600">({hiddenCount} with warnings hidden)</span>
                  )}
                </label>
                <span className="text-sm text-gray-500">{selectedInTab} of {newRows.length} selected</span>
              </div>
            </div>

            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs uppercase text-gray-500 tracking-wide">
                  <tr>
                    <th className="px-3 py-2 text-left w-8">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        ref={el => { if (el) el.indeterminate = someSelected && !allSelected; }}
                        onChange={() => toggleSelectAll(newRows)}
                        title="Select all visible rows in this tab"
                      />
                    </th>
                    <th className="px-3 py-2 text-left">Work Order #</th>
                    <th className="px-3 py-2 text-left">Address</th>
                    <th className="px-3 py-2 text-right">Paid to Us</th>
                    <th className="px-3 py-2 text-right">Paid to Sub</th>
                    <th className="px-3 py-2 text-left">Sub / Contact</th>
                    <th className="px-3 py-2 text-left">Invoice Contact</th>
                    <th className="px-3 py-2 text-left">Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {newRows.map((row) => (
                    <tr key={rowKey(row)} className={`hover:bg-blue-50 ${selectedKeys.has(rowKey(row)) ? 'bg-blue-50/40' : ''}`}>
                      <td className="px-3 py-2">
                        <input type="checkbox" checked={selectedKeys.has(rowKey(row))} onChange={() => toggleRow(row)} />
                      </td>
                      <td className="px-3 py-2 font-medium text-gray-800">{row.workOrderNumber}</td>
                      <td className="px-3 py-2">
                        <input
                          className="border border-gray-200 rounded px-2 py-1 w-32 text-sm"
                          value={fieldValue(row, 'address') || ''}
                          onChange={(e) => updateOverride(row, 'address', e.target.value)}
                        />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <AmountInput row={row} field="amountPaidToUs" rawField="amountPaidToUsRaw" overrides={overrides} updateOverride={updateOverride} />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <AmountInput row={row} field="amountPaidToSub" rawField="amountPaidToSubRaw" overrides={overrides} updateOverride={updateOverride} />
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-medium">{row.subName}</div>
                        {row.subcontractorContactMatchName
                          ? <div className="text-xs text-green-700">→ {row.subcontractorContactMatchName}</div>
                          : <div className="text-xs text-red-500 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />no Xero match</div>
                        }
                      </td>
                      <td className="px-3 py-2">
                        <div className="text-xs text-gray-600">{row.clientName}</div>
                        <div className="text-xs text-gray-400">{row.department}</div>
                        {row.clientContactMatchName
                          ? <div className="text-xs text-green-700">→ {row.clientContactMatchName}</div>
                          : <div className="text-xs text-red-500 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />no Xero match</div>
                        }
                      </td>
                      <td className="px-3 py-2 text-gray-500 text-xs">{row.notes}</td>
                    </tr>
                  ))}

                  {existingRows.map((row) => (
                    <tr key={rowKey(row)} className="bg-gray-50 opacity-50">
                      <td className="px-3 py-2"><input type="checkbox" disabled /></td>
                      <td className="px-3 py-2 font-medium line-through text-gray-400">{row.workOrderNumber}</td>
                      <td className="px-3 py-2 text-gray-400" colSpan={7}>Already exists — will be skipped</td>
                    </tr>
                  ))}

                  {tabRows.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-4 py-6 text-center text-gray-400">No pending rows in this tab.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {/* Approve button */}
      {rows.length > 0 && (
        <div className="flex justify-end pt-2">
          <button
            onClick={handleApprove}
            disabled={approving || selectedKeys.size === 0 || unresolvedWarnings.length > 0}
            title={unresolvedWarnings.length > 0 ? 'Fix the orange dollar fields before approving' : undefined}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            {approving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                Importing...
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                Approve & Import {selectedKeys.size} Work Order{selectedKeys.size === 1 ? '' : 's'}
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default SheetImport;
